import { useEffect, useRef, useState } from 'react';
import Persona from 'persona';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { X, Loader2, ShieldCheck, IdCard } from 'lucide-react';

/**
 * Persona Embedded Web SDK modal (KYC-lite: ID + selfie + liveness).
 *
 * Backend creates the inquiry (POST /api/verify/persona/inquiry) and returns
 * { inquiry_id, session_token, environment_id, status }. Persona's Client
 * Widget renders its OWN fullscreen modal on top of the page — this component
 * just shows a small loading/status sheet while the SDK is booting or after
 * completion, and mounts/destroys the Persona client on lifecycle events.
 *
 * The webhook is the source of truth for verified state — after onComplete,
 * we poll /verify/persona/status a few times so the UI badge updates once
 * Persona finishes async processing.
 */
export default function PersonaVerifyModal({ open, onClose, onVerified }) {
  const [phase, setPhase] = useState('idle'); // idle | loading | inflight | processing | done | error
  const [errorMsg, setErrorMsg] = useState('');
  const clientRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setPhase('loading');
    setErrorMsg('');

    (async () => {
      try {
        const { data } = await api.post('/verify/persona/inquiry', {});
        if (cancelled) return;
        const { inquiry_id, session_token, environment_id } = data || {};
        if (!inquiry_id) {
          setPhase('error');
          setErrorMsg('Could not start verification. Please try again.');
          return;
        }

        const cfg = { inquiryId: inquiry_id };
        if (session_token) cfg.sessionToken = session_token;
        if (environment_id) cfg.environmentId = environment_id;

        const client = new Persona.Client({
          ...cfg,
          onReady: () => {
            if (cancelled) return;
            setPhase('inflight');
            try { client.open(); } catch { /* noop */ }
          },
          onComplete: async () => {
            setPhase('processing');
            try { client.destroy(); } catch { /* noop */ }
            // Webhook is authoritative — poll status briefly.
            let verified = false;
            for (let i = 0; i < 6; i++) {
              try {
                const r = await api.get('/verify/persona/status');
                if (r.data?.verified || r.data?.state === 'approved') { verified = true; break; }
                if (['failed', 'declined', 'expired'].includes(r.data?.state)) break;
              } catch { /* keep polling */ }
              await new Promise((res) => setTimeout(res, 1500));
            }
            setPhase('done');
            if (verified) {
              toast.success('Identity verified — blue badge added ✓');
              onVerified?.();
            } else {
              toast.info("We're finishing your verification — check back in a minute.");
            }
            onClose?.();
          },
          onCancel: () => {
            try { client.destroy(); } catch { /* noop */ }
            setPhase('idle');
            onClose?.();
          },
          onError: (err) => {
            console.error('Persona SDK error', err);
            try { client.destroy(); } catch { /* noop */ }
            setPhase('error');
            setErrorMsg(err?.message || 'Persona could not load. Please try again.');
          },
        });
        clientRef.current = client;
      } catch (err) {
        if (cancelled) return;
        setPhase('error');
        setErrorMsg(err?.response?.data?.detail || 'Unable to start verification.');
      }
    })();

    return () => {
      cancelled = true;
      try { clientRef.current?.destroy(); } catch { /* noop */ }
      clientRef.current = null;
    };
  }, [open, onClose, onVerified]);

  if (!open) return null;

  // Persona's own modal renders in its own root, so our shell only shows during
  // loading/processing/error states.
  const showShell = phase !== 'inflight';
  if (!showShell) return null;

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="persona-modal-backdrop"
    >
      <div
        className="w-full max-w-sm bg-white rounded-2xl shadow-2xl border border-black/[0.06] p-6"
        data-testid="persona-modal"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[rgba(30,79,216,0.08)] flex items-center justify-center">
              {phase === 'done' ? (
                <ShieldCheck className="w-5 h-5 text-[#15803D]" />
              ) : (
                <IdCard className="w-5 h-5 text-[#1E4FD8]" />
              )}
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">
                IDENTITY VERIFICATION
              </p>
              <h2 className="font-display text-[18px] font-[600] text-[#0F0F10] leading-tight">
                {phase === 'done' ? 'All set' : 'Verify with government ID'}
              </h2>
            </div>
          </div>
          <button
            onClick={() => { try { clientRef.current?.destroy(); } catch { /* noop */ } onClose?.(); }}
            className="w-8 h-8 rounded-full hover:bg-black/[0.05] flex items-center justify-center text-[#4B4B52]"
            data-testid="persona-modal-close"
            aria-label="Close verification"
          >
            <X size={18} />
          </button>
        </div>

        {phase === 'loading' && (
          <div className="py-6 text-center" data-testid="persona-modal-loading">
            <Loader2 className="w-6 h-6 animate-spin text-[#1E4FD8] mx-auto mb-3" />
            <p className="text-sm text-[#4B4B52]">Preparing your secure session…</p>
          </div>
        )}

        {phase === 'processing' && (
          <div className="py-6 text-center" data-testid="persona-modal-processing">
            <Loader2 className="w-6 h-6 animate-spin text-[#1E4FD8] mx-auto mb-3" />
            <p className="text-sm text-[#4B4B52]">
              Finishing up your verification — hang tight.
            </p>
          </div>
        )}

        {phase === 'error' && (
          <div className="py-4" data-testid="persona-modal-error">
            <p className="text-sm text-[#B91C1C] mb-4">{errorMsg}</p>
            <button
              onClick={() => onClose?.()}
              className="w-full bg-black text-white py-3 rounded-xl font-semibold text-sm"
              data-testid="persona-modal-error-close"
            >
              Close
            </button>
          </div>
        )}

        {phase === 'done' && (
          <div className="py-4 text-center" data-testid="persona-modal-done">
            <p className="text-sm text-[#4B4B52] mb-4">
              Your verification is being processed. If approved, you&apos;ll see the blue
              badge on your profile shortly.
            </p>
            <button
              onClick={() => onClose?.()}
              className="w-full bg-black text-white py-3 rounded-xl font-semibold text-sm"
              data-testid="persona-modal-done-close"
            >
              Back to profile
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
